/**
 * Shopify Customer Account API — Web public client (OAuth + PKCE).
 * Client Secret は使用しない。トークンは sessionStorage のみ。
 */
(() => {
  const KEY = {
    verifier: 'miyabi_ca_code_verifier',
    state: 'miyabi_ca_oauth_state',
    nonce: 'miyabi_ca_oauth_nonce',
    returnTo: 'miyabi_ca_oauth_return_to',
    accessToken: 'miyabi_ca_access_token',
    refreshToken: 'miyabi_ca_refresh_token',
    idToken: 'miyabi_ca_id_token',
    expiresAt: 'miyabi_ca_expires_at',
  };

  let discoveryCache = null;
  let discoveryPromise = null;

  function getConfig() {
    const el = document.querySelector('[data-customer-account-web-auth]');
    if (!el) return null;
    return {
      clientId: (el.getAttribute('data-client-id') || '').trim(),
      shopDomain: (el.getAttribute('data-shop-domain') || window.location.hostname || '').trim(),
      callbackPath: (el.getAttribute('data-callback-path') || '/pages/customer-account-callback').trim(),
      logoutReturnUrl: (el.getAttribute('data-logout-return-url') || '/').trim(),
      scope: (el.getAttribute('data-scope') || 'openid email customer-account-api:full').trim(),
    };
  }

  function ssGet(key) {
    try {
      return sessionStorage.getItem(key);
    } catch (e) {
      return null;
    }
  }

  function ssSet(key, value) {
    try {
      if (value == null || value === '') sessionStorage.removeItem(key);
      else sessionStorage.setItem(key, value);
    } catch (e) {
      // sessionStorage 不可でも致命扱いしない
    }
  }

  function ssRemove(key) {
    try {
      sessionStorage.removeItem(key);
    } catch (e) {
      /* noop */
    }
  }

  function toBase64Url(buffer) {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.length; i += 1) binary += String.fromCharCode(bytes[i]);
    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
  }

  function randomString(byteLength) {
    const bytes = new Uint8Array(byteLength);
    crypto.getRandomValues(bytes);
    return toBase64Url(bytes);
  }

  async function sha256Base64Url(plain) {
    const data = new TextEncoder().encode(plain);
    const digest = await crypto.subtle.digest('SHA-256', data);
    return toBase64Url(digest);
  }

  function absoluteUrl(pathOrUrl) {
    if (!pathOrUrl) return window.location.origin + '/';
    if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
    const path = pathOrUrl.startsWith('/') ? pathOrUrl : `/${pathOrUrl}`;
    return `${window.location.origin}${path}`;
  }

  async function discoverEndpoints(shopDomain) {
    if (discoveryCache) return discoveryCache;
    if (discoveryPromise) return discoveryPromise;

    const domain = (shopDomain || '').replace(/^https?:\/\//, '').replace(/\/$/, '');
    const url = `https://${domain}/.well-known/openid-configuration`;

    discoveryPromise = fetch(url, {
      method: 'GET',
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    })
      .then(async (response) => {
        if (!response.ok) {
          throw new Error('openid_discovery_failed');
        }
        const json = await response.json();
        if (!json.authorization_endpoint || !json.token_endpoint) {
          throw new Error('openid_discovery_incomplete');
        }
        discoveryCache = json;
        return json;
      })
      .finally(() => {
        discoveryPromise = null;
      });

    return discoveryPromise;
  }

  function clearAuthSession() {
    ssRemove(KEY.accessToken);
    ssRemove(KEY.refreshToken);
    ssRemove(KEY.idToken);
    ssRemove(KEY.expiresAt);
  }

  function clearPkceTemps() {
    ssRemove(KEY.verifier);
    ssRemove(KEY.state);
    ssRemove(KEY.nonce);
  }

  function getStoredAccessToken() {
    const token = ssGet(KEY.accessToken);
    if (!token) return null;
    const expiresAt = Number(ssGet(KEY.expiresAt) || 0);
    if (expiresAt && Date.now() >= expiresAt - 30_000) {
      return null;
    }
    return token;
  }

  function storeTokenResponse(payload) {
    if (!payload || !payload.access_token) {
      throw new Error('token_response_invalid');
    }
    ssSet(KEY.accessToken, payload.access_token);
    if (payload.refresh_token) ssSet(KEY.refreshToken, payload.refresh_token);
    else ssRemove(KEY.refreshToken);
    if (payload.id_token) ssSet(KEY.idToken, payload.id_token);
    else ssRemove(KEY.idToken);

    const expiresIn = Number(payload.expires_in);
    if (Number.isFinite(expiresIn) && expiresIn > 0) {
      ssSet(KEY.expiresAt, String(Date.now() + expiresIn * 1000));
    } else {
      ssRemove(KEY.expiresAt);
    }
  }

  async function startLogin(options = {}) {
    const config = getConfig();
    if (!config || !config.clientId) {
      throw new Error('client_id_missing');
    }

    const returnTo = absoluteUrl(options.returnTo || window.location.href);
    const codeVerifier = randomString(64);
    const state = randomString(32);
    const nonce = randomString(32);
    const codeChallenge = await sha256Base64Url(codeVerifier);

    ssSet(KEY.verifier, codeVerifier);
    ssSet(KEY.state, state);
    ssSet(KEY.nonce, nonce);
    ssSet(KEY.returnTo, returnTo);

    const endpoints = await discoverEndpoints(config.shopDomain);
    const authUrl = new URL(endpoints.authorization_endpoint);
    authUrl.searchParams.set('client_id', config.clientId);
    authUrl.searchParams.set('response_type', 'code');
    authUrl.searchParams.set('redirect_uri', absoluteUrl(config.callbackPath));
    authUrl.searchParams.set('scope', config.scope);
    authUrl.searchParams.set('state', state);
    authUrl.searchParams.set('nonce', nonce);
    authUrl.searchParams.set('code_challenge', codeChallenge);
    authUrl.searchParams.set('code_challenge_method', 'S256');

    window.location.assign(authUrl.toString());
  }

  async function handleCallback(searchParams) {
    const config = getConfig();
    if (!config || !config.clientId) {
      throw new Error('client_id_missing');
    }

    const error = searchParams.get('error');
    if (error) {
      clearPkceTemps();
      throw new Error('oauth_denied');
    }

    const code = searchParams.get('code');
    const state = searchParams.get('state');
    const expectedState = ssGet(KEY.state);
    const codeVerifier = ssGet(KEY.verifier);

    if (!code || !state || !expectedState || state !== expectedState) {
      clearPkceTemps();
      throw new Error('oauth_state_mismatch');
    }
    if (!codeVerifier) {
      clearPkceTemps();
      throw new Error('oauth_verifier_missing');
    }

    const endpoints = await discoverEndpoints(config.shopDomain);
    const body = new URLSearchParams();
    body.set('grant_type', 'authorization_code');
    body.set('client_id', config.clientId);
    body.set('code', code);
    body.set('redirect_uri', absoluteUrl(config.callbackPath));
    body.set('code_verifier', codeVerifier);

    const response = await fetch(endpoints.token_endpoint, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: body.toString(),
      cache: 'no-store',
    });

    if (!response.ok) {
      clearPkceTemps();
      clearAuthSession();
      throw new Error('token_exchange_failed');
    }

    const payload = await response.json();
    storeTokenResponse(payload);
    clearPkceTemps();

    const returnTo = ssGet(KEY.returnTo) || absoluteUrl('/');
    ssRemove(KEY.returnTo);
    return { returnTo };
  }

  async function refreshAccessToken() {
    const config = getConfig();
    const refreshToken = ssGet(KEY.refreshToken);
    if (!config || !config.clientId || !refreshToken) return null;

    const endpoints = await discoverEndpoints(config.shopDomain);
    const body = new URLSearchParams();
    body.set('grant_type', 'refresh_token');
    body.set('client_id', config.clientId);
    body.set('refresh_token', refreshToken);

    const response = await fetch(endpoints.token_endpoint, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: body.toString(),
      cache: 'no-store',
    });

    if (!response.ok) {
      clearAuthSession();
      return null;
    }

    const payload = await response.json();
    storeTokenResponse(payload);
    return payload.access_token || null;
  }

  async function getValidAccessToken() {
    const current = getStoredAccessToken();
    if (current) return current;
    return refreshAccessToken();
  }

  async function logout() {
    const config = getConfig();
    if (!config) {
      clearAuthSession();
      clearPkceTemps();
      window.location.assign('/');
      return;
    }

    const idToken = ssGet(KEY.idToken);

    try {
      const endpoints = await discoverEndpoints(config.shopDomain);
      if (endpoints.end_session_endpoint) {
        const logoutUrl = new URL(endpoints.end_session_endpoint);
        if (idToken) logoutUrl.searchParams.set('id_token_hint', idToken);
        logoutUrl.searchParams.set('post_logout_redirect_uri', absoluteUrl(config.logoutReturnUrl));
        clearAuthSession();
        clearPkceTemps();
        window.location.assign(logoutUrl.toString());
        return;
      }
    } catch (e) {
      /* fall through */
    }

    clearAuthSession();
    clearPkceTemps();
    window.location.assign(absoluteUrl(config.logoutReturnUrl));
  }

  window.MiyabiCustomerAccountWebAuth = {
    getConfig,
    startLogin,
    handleCallback,
    getValidAccessToken,
    clearAuthSession,
    logout,
    KEY,
  };
})();
